import { ShieldCheck, Sparkles, Lock, Zap, ArrowRight, FileSearch, Scale } from "lucide-react";
import { Link } from "react-router-dom";

export default function HeroSection({ onStartClick }) {
  return (
    <section
      className="relative overflow-hidden border-b border-slate-200"
      data-testid="hero-section"
    >
      {/* Background hero image (very subtle, blurred) */}
      <div
        aria-hidden
        className="absolute inset-0 hero-glow"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-grid-pattern opacity-50"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[url('https://static.prod-images.emergentagent.com/jobs/8daf0132-622b-4f1a-adc4-4d207b199d2c/images/41a0ce544cdb492e78e67e6865d31e06a63eff152ed769ae500725a6ece3948b.png')] bg-cover bg-center opacity-[0.07] blur-2xl"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-8 pb-16 sm:pt-20 sm:pb-28">
        {/* Top nav */}
        <nav className="flex items-center justify-between mb-10 sm:mb-16 fade-up" data-testid="top-nav">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center shadow-sm">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-heading font-bold text-base sm:text-lg text-slate-900">EnergieKontract</span>
          </div>
          <div className="hidden sm:flex items-center gap-6 text-sm text-slate-600 font-medium">
            <a href="#hoe-werkt-het" className="hover:text-blue-700 transition-colors" data-testid="nav-how">Hoe werkt het</a>
            <a href="#upload" className="hover:text-blue-700 transition-colors" data-testid="nav-upload">Check je contract</a>
            <Link to="/vergelijk" className="hover:text-blue-700 transition-colors" data-testid="nav-compare">Direct vergelijken</Link>
            <Link to="/zakelijk" className="hover:text-blue-700 transition-colors" data-testid="nav-b2b">Zakelijk</Link>
            <a href="#faq" className="hover:text-blue-700 transition-colors" data-testid="nav-faq">FAQ</a>
          </div>
          <button
            onClick={onStartClick}
            className="bg-blue-700 hover:bg-blue-800 text-white text-xs sm:text-sm font-medium px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-lg shadow-sm transition-colors whitespace-nowrap"
            data-testid="hero-cta-nav"
          >
            <span className="sm:hidden">Start check</span>
            <span className="hidden sm:inline">Start gratis check</span>
          </button>
        </nav>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up fade-up-delay-1">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
              <Sparkles className="h-3.5 w-3.5" />
              De slimste energiecontract check
            </span>
            <h1 className="mt-6 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.05]">
              Upload je energiecontract.
              <br />
              <span className="text-blue-700">Wij vinden de addertjes</span>
              <br />
              en je besparingen.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Wij scannen je PDF op verborgen leveringskosten, slechte teruglever&shy;voorwaarden voor zonnepanelen,
              automatische verlengingen en wurgcontracten. Binnen 30 seconden.
            </p>

            {/* Two paths — analyse or compare directly */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
              <button
                onClick={onStartClick}
                className="group relative bg-blue-700 hover:bg-blue-800 text-white rounded-xl px-5 py-4 shadow-[0_8px_24px_-12px_rgba(29,78,216,0.5)] transition-all hover:-translate-y-0.5 text-left"
                data-testid="hero-cta-primary"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/15 grid place-items-center flex-shrink-0">
                    <FileSearch className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-base leading-tight">Check je contract</div>
                    <div className="text-[12px] text-blue-100/90 mt-0.5">AI analyse · €0 · 30 sec</div>
                  </div>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>
              <Link
                to="/vergelijk"
                className="group relative bg-white border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-900 rounded-xl px-5 py-4 transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-compare"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 grid place-items-center flex-shrink-0 group-hover:bg-white">
                    <Scale className="h-5 w-5 text-blue-700" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-base leading-tight">Direct vergelijken</div>
                    <div className="text-[12px] text-slate-600 mt-0.5">9 aanbieders · in 1 oogopslag</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-blue-700 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-1.5" data-testid="trust-anoniem">
                <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span>100% Anoniem</span>
              </div>
              <span className="hidden sm:inline text-slate-300">•</span>
              <div className="flex items-center gap-1.5" data-testid="trust-avg">
                <Lock className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span>AVG Proof — bestand wordt niet opgeslagen</span>
              </div>
              <span className="hidden sm:inline text-slate-300">•</span>
              <div className="flex items-center gap-1.5" data-testid="trust-result">
                <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span>Rapport in 30 seconden</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-delay-2">
            <HeroVisualCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisualCard() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-blue-100/60 via-transparent to-emerald-100/60 rounded-[2rem] blur-2xl" aria-hidden />
      <div className="relative bg-white border border-slate-200 rounded-2xl shadow-[0_24px_80px_-24px_rgba(15,23,42,0.18)] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
          </div>
          <span className="ml-3 text-xs font-medium text-slate-500">analyse_rapport.pdf</span>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Resultaat preview</div>
            <h3 className="mt-1 font-heading text-xl font-semibold text-slate-900">
              Jaarlijkse besparing gevonden
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Huidig p/m" value="€245" tone="slate" />
            <MiniStat label="Mogelijk p/jr" value="€480 ▼" tone="emerald" />
          </div>

          <div className="space-y-2 pt-2">
            <PreviewRow color="amber" text="Hoge vaste leveringskosten gevonden" />
            <PreviewRow color="red" text="Nadelige terugleververgoeding" />
            <PreviewRow color="amber" text="Automatische verlenging zonder waarschuwing" />
          </div>

          <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Risicoscore</span>
            <span className="text-sm font-semibold bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
              Gemiddeld
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const toneClass =
    tone === "emerald"
      ? "bg-emerald-50 border-emerald-100 text-emerald-700"
      : "bg-slate-50 border-slate-100 text-slate-900";
  return (
    <div className={`rounded-xl border p-3 ${toneClass}`}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-heading font-bold">{value}</div>
    </div>
  );
}

function PreviewRow({ color, text }) {
  const dot =
    color === "red"
      ? "bg-red-500"
      : color === "amber"
      ? "bg-amber-500"
      : "bg-emerald-500";
  return (
    <div className="flex items-start gap-2 text-sm text-slate-700">
      <span className={`mt-1.5 h-2 w-2 rounded-full ${dot}`} />
      <span>{text}</span>
    </div>
  );
}
